import _ from 'lodash';
import {
  mslAllowedExposureHours,
  PackagingOptions,
  Shipment,
  ShipmentBase,
  ShipmentLite,
  ShipmentType,
  StockLocation,
  StockLocationType,
} from 'types/inventory';
import { Org } from 'types/org';
import { ProductionRun } from 'types/production';
import { GenericObject } from 'types';
import { hoursToHoursOrDays } from 'utils/dates';
import { packageTypes } from 'types/part';
import { Dispatch, SetStateAction } from 'react';
import { PropertySubsetValidator } from 'types/api';
import { OrgSupplier, OrgSupplierLite, PublicSupplier } from 'types/suppliers';
import { PermissionScope } from 'types/apps';
import {
  CustomerPickupGroupHeader,
  WarehousePickupOption,
  CofactrPickupOption,
} from 'types/purchasing';
import { ALL_APP_IDS } from 'constants/appConstants';
import {
  UNLIMITED_QUANTITY,
  UNLIMITED_QUANTITY_STRING,
} from 'constants/procurementConstants';
import { API_URL } from 'constants/envConstants';

// @ts-ignore
export const camelize = (obj): any =>
  _.transform(obj, (acc, value, key, target) => {
    // @ts-ignore
    const camelKey = _.isArray(target) ? key : _.camelCase(key);
    // @ts-ignore
    acc[camelKey] = _.isObject(value) ? camelize(value) : value;
  });

// @ts-ignore
export const camelizeVars = (obj): any =>
  _.transform(obj, (acc, value, key, target) => {
    if (key === 'var') {
      acc[key] = _.camelCase(value);
    } else {
      acc[key] = _.isObject(value) ? camelizeVars(value) : value;
    }
  });

/**
 * Converts specific field names to their snake_case equivalents for V2 Org Parts.
 *
 * @remarks
 * This is very hacky, but the function is splitting this key into `v_2_org_parts`
 * I don't want to make sweeping changes to the main snakeCase function, because it could easily break many things - KM
 *
 * @param {string | number | symbol} field - The field name to convert.
 * @returns {string | null} The snake_case equivalent of the field name, or null if the field is not recognized.
 *
 * @example
 * // Returns 'v2_org_parts'
 * getSnakeCaseV2OrgPartFields('v2OrgParts');
 *
 * @example
 * // Returns 'v2_org_part'
 * getSnakeCaseV2OrgPartFields('v2OrgPart');
 *
 * @example
 * // Returns 'alt_v2_org_parts'
 * getSnakeCaseV2OrgPartFields('altV2OrgParts');
 *
 * @example
 * // Returns 'unapproved_alt_v2_org_parts'
 * getSnakeCaseV2OrgPartFields('unapprovedAltV2OrgParts');
 *
 * @example
 * // Returns 'v2_org_part_id'
 * getSnakeCaseV2OrgPartFields('v2OrgPartId')
 *
 * @example
 * // Returns null
 * getSnakeCaseV2OrgPartFields('someOtherField');
 */
export const getSnakeCaseV2OrgPartFields = (
  field: string | number | symbol
) => {
  if (typeof field !== 'string') {
    return null;
  }
  if (field === 'v2OrgParts') {
    return 'v2_org_parts';
  }
  if (field === 'v2OrgPart') {
    return 'v2_org_part';
  }
  if (field === 'altV2OrgParts') {
    return 'alt_v2_org_parts';
  }
  if (field === 'unapprovedAltV2OrgParts') {
    return 'unapproved_alt_v2_org_parts';
  }
  if (field === 'v2OrgPartId') {
    return 'v2_org_part_id';
  }
  if (field === 'v2OrgPartIds') {
    return 'v2_org_part_ids';
  }
  if (field === 'v2KitRequestLines') {
    return 'v2_kit_request_lines';
  }
  return null;
};

/**
 * Converts a given field name to its lodash snake_case equivalent, with special handling for V2 Org Part fields.
 *
 * @param {string} field - The field name to convert.
 * @returns {string} The snake_case equivalent of the field name.
 *
 * @example
 * // Returns 'v2_org_parts'
 * snakeCasedOrgPartHelper('v2OrgParts');
 *
 * @example
 * // Returns 'v2_org_part'
 * snakeCasedOrgPartHelper('v2OrgPart');
 *
 * @example
 * // Returns 'alt_v2_org_parts'
 * snakeCasedOrgPartHelper('altV2OrgParts');
 *
 * @example
 * // Returns 'unapproved_alt_v2_org_parts'
 * snakeCasedOrgPartHelper('unapprovedAltV2OrgParts');
 *
 * @example
 * // Returns 'some_other_field'
 * snakeCasedOrgPartHelper('someOtherField');
 */
export const snakeCasedOrgPartHelper = (field: string): string => {
  let snakeCasedItem;
  const snakeCasedV2OrgPartField = getSnakeCaseV2OrgPartFields(field);
  if (snakeCasedV2OrgPartField) {
    snakeCasedItem = snakeCasedV2OrgPartField;
  } else {
    snakeCasedItem = _.snakeCase(field);
  }
  return snakeCasedItem;
};

// @ts-ignore
export const snakeCase = (obj) =>
  _.transform(obj, (acc, value, key, target) => {
    let snakeKey;

    const snakeCasedV2OrgPartField = getSnakeCaseV2OrgPartFields(key);
    if (snakeCasedV2OrgPartField) {
      snakeKey = snakeCasedV2OrgPartField;
    } else {
      // @ts-ignore
      snakeKey = _.isArray(target) ? key : _.snakeCase(key);
    }
    // @ts-ignore
    // if the key is 'specs' we don't snake case the value because the keys in that specs object should be maintained as human readable strings within V2 Org Parts
    // Date objects should not be recursively processed as they don't have enumerable properties
    acc[snakeKey] =
      _.isObject(value) && key !== 'specs' && !(value instanceof Date)
        ? snakeCase(value)
        : value;
  });

export const removeUndefined = (obj: any): any => {
  if (_.isString(obj)) return obj;
  if (obj instanceof Date) return obj;
  if (Array.isArray(obj)) {
    return _.map(obj, removeUndefined);
  }
  return _.omitBy(obj, (val) => val === undefined);
};

// type=facility for which customer_location=False or customer_location=True and ext_customer_id=current org
export const stockLocationsDefaultFilter = (rows: StockLocation[]) =>
  _.filter(rows, (row) => row.locationType === StockLocationType.FACILITY);

type errHandler = (
  errMessage: string
) => void | Dispatch<SetStateAction<string | null>>;

export type ErrorType = Error & {
  status: number;
  originalStatus?: number;
  data?:
    | string
    | {
        message?: string;
      };
  message?: string;
};

export const generalErrorMessage = `There was an error completing this action. We've alerted our team, and they're looking into it. Please refresh the page, or try again later.`;

export const processErrorMessage = (error: unknown, errHandler: errHandler) => {
  const err = error as ErrorType;
  if (
    err?.status === 404 ||
    err.status >= 500 ||
    (err?.originalStatus && err.originalStatus >= 500)
  ) {
    errHandler(generalErrorMessage);
  } else if (
    err?.data &&
    typeof err?.data === 'string' &&
    err.data?.includes('<!DOCTYPE html>')
  ) {
    errHandler(generalErrorMessage);
  } else if (
    typeof err?.data === 'object' &&
    typeof err.data.message === 'string'
  ) {
    errHandler(err?.data?.message);
  } else if (typeof err?.message === 'string') {
    errHandler(err?.message);
  } else if (typeof err?.data === 'string') {
    errHandler(err?.data);
  } else {
    errHandler(generalErrorMessage);
  }
};

export const handleErr = (error: unknown, errHandler: errHandler) => {
  processErrorMessage(error, errHandler);
  // eslint-disable-next-line no-throw-literal -- This preserves the previous behavior of `handleErr`
  throw error as ErrorType;
};

export function getCookie(name: string) {
  // It's warning about unnecessary escapes and regex scares me so I'm murdering TS instead
  function escape(s: string) {
    // eslint-disable-next-line
    return s.replace(/([.*+?^$(){}|\[\]\/\\])/g, '\\$1');
  }
  const match = document.cookie.match(
    // eslint-disable-next-line
    RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)')
  );
  return match ? match[1] : null;
}

export const formatPrice = (
  value: number | string | undefined | null,
  precision?: number
) =>
  value || value === 0
    ? parseFloat(value as unknown as string).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: precision || 6,
        minimumFractionDigits: precision || 2,
      })
    : '-';

export const copyClipboard = (value: string | null | undefined) =>
  value ? navigator.clipboard.writeText(value) : undefined;

export function checkIfValidUUID(str: string) {
  // Regular expression to check if string is a valid UUID
  const regexExp =
    /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
  return regexExp.test(str);
}

// Written for Segment track events
// Not intended for Group, Page, Identify, etc
export function analyticsTrack(eventName: string, eventData: GenericObject) {
  if (window.analytics) {
    window.analytics.track(eventName, eventData);
  }
}

export function formatOrReturnDefault({
  value,
  formatFunction,
  defaultValue = '-',
}: {
  value: any;
  formatFunction: Function;
  defaultValue?: string;
}) {
  return value ? formatFunction(value) : defaultValue;
}

export function mslRemainingFloorLife(msl: string, mslFloorHours: number) {
  if (!msl) {
    return null;
  }
  if (msl === '1') {
    return Infinity;
  }
  return (
    _.get(mslAllowedExposureHours, _.toLower(msl), 0) - (mslFloorHours || 0)
  );
}

export function mslFloorLifeText(
  msl: string | null | undefined,
  mslFloorHours: number | null | undefined
) {
  const mslHours = !mslFloorHours ? 0 : mslFloorHours;
  if (!msl) {
    return 'Unlimited';
  }
  if (!mslRemainingFloorLife(msl, mslHours)) {
    return 'Unlimited';
  }
  if (mslRemainingFloorLife(msl, mslHours) === Infinity) {
    return 'Unlimited';
  }
  if ((mslRemainingFloorLife(msl, mslHours) || 0) <= 23) {
    return 'Bake Required';
  }
  const timeRemaining = hoursToHoursOrDays(
    mslRemainingFloorLife(msl, mslHours) || 0
  );
  return `${timeRemaining.value.toLocaleString()} ${pluralizeNoun(
    timeRemaining.unit,
    timeRemaining.value
  )}`;
}

export function mslFloorLifeColumnText(value: number | null) {
  if (!value) {
    return 'Unlimited';
  }
  if (value === Infinity) {
    return 'Unlimited';
  }
  if (value <= 23) {
    return 'Bake Required';
  }
  const timeRemaining = hoursToHoursOrDays(value || 0);
  return `${timeRemaining.value.toLocaleString()} ${pluralizeNoun(
    timeRemaining.unit,
    timeRemaining.value
  )}`;
}

export function getDefaultShippingMethod({
  org,
  productionRun,
}: {
  org?: Org;
  productionRun?: PropertySubsetValidator<
    ProductionRun,
    'defaultShippingCarrier' | 'defaultShippingService'
  >;
}) {
  if (productionRun) {
    return productionRun?.defaultShippingService &&
      productionRun?.defaultShippingCarrier
      ? `${productionRun.defaultShippingCarrier}_${productionRun.defaultShippingService}`
      : '';
  }
  if (org) {
    return org?.defaultShippingService && org?.defaultShippingCarrier
      ? `${org.defaultShippingCarrier}_${org.defaultShippingService}`
      : '';
  }
  return '';
}

export function getShipmentMethod(
  obj: { carrier?: string | null; service?: string | null } | null | undefined
) {
  if (!obj) return '';
  if (obj.carrier && obj.service) {
    return `${obj.carrier}_${obj.service}`;
  }
  return '';
}

export function getShipmentCarrierAndService(method: string) {
  const [defaultShippingCarrier, defaultShippingService] = method.split('_');
  return { defaultShippingCarrier, defaultShippingService };
}

export function pluralizeNoun(word: string, value: number) {
  return `${word}${value === 1 ? '' : 's'}`;
}

export function getPickupOptions(hasWarehousePermission: boolean) {
  if (hasWarehousePermission) {
    return [CustomerPickupGroupHeader, WarehousePickupOption];
  }

  return [CustomerPickupGroupHeader, CofactrPickupOption];
}

export function pluralizeWordOption(
  singular: string,
  plural: string,
  value: number
) {
  return value === 1 ? singular : plural;
}

export function formatListWithAnd(list: string[]) {
  const formatter = new Intl.ListFormat('gb', {
    style: 'long',
    type: 'conjunction',
  });
  return formatter.format(list);
}

export function formatListWithOr(list: string[]) {
  const formatter = new Intl.ListFormat('gb', {
    style: 'long',
    type: 'disjunction',
  });
  return formatter.format(list);
}

export function formatListWithMore(list: string[], trimLength: number) {
  const compactList = _.compact(list);
  if (!compactList.length) {
    return '';
  }
  if (compactList.length <= trimLength) {
    return _.join(compactList, ', ');
  }
  return `${_.join(compactList.slice(0, trimLength), ', ')} +${(
    compactList.length - trimLength
  ).toLocaleString('en-US')} more`;
}

export function getShipmentPickerLabel(
  shipment: Shipment | ShipmentBase | ShipmentLite
) {
  if (shipment.shipmentType === ShipmentType.KIT_WITHIN_WAREHOUSE) {
    return `Kit at ${
      shipment.shipFrom?.externalName || shipment.shipFrom?.name
    } (${shipment.shipmentCode}) ${shipment.notes}`;
  }
  return `${
    shipment.shipFrom?.externalName || shipment.shipFrom?.name || '-'
  } → ${
    shipment.shipTo?.externalName ||
    shipment.shipTo?.name ||
    shipment.shipTo?.address?.company ||
    shipment.shipTo?.address?.name ||
    shipment.address?.company ||
    shipment.address?.name ||
    '-'
  } (${shipment.shipmentCode}) ${shipment.notes}`;
}

export function removeNonAlphanumericOrPeriod(
  inputString: string | null | undefined
) {
  if (!inputString) {
    return inputString;
  }
  return inputString.replace(/[^a-zA-Z0-9.]/g, '');
}

export const packageFromDescription = (description: string) => {
  const cleanedDescription = (
    removeNonAlphanumericOrPeriod(description) ?? ''
  ).toLowerCase();
  let foundPackage = null;
  _.forEach(packageTypes, (p) => {
    const cleanedPackage = _.toLower(removeNonAlphanumericOrPeriod(p) ?? '');
    if (_.includes(cleanedDescription, cleanedPackage)) {
      foundPackage = p;
      return false;
    }
    return true;
  });
  return foundPackage;
};

export const packageFromOfferPackage = (offerPackage: string | null) => {
  if (!offerPackage) return null;
  const lowerPackage = _.toLower(offerPackage);
  if (lowerPackage.includes(PackagingOptions.REEL))
    return PackagingOptions.REEL;
  if (lowerPackage.includes('tape')) return PackagingOptions.TAPE_SINGLE;
  if (lowerPackage.includes(PackagingOptions.TRAY))
    return PackagingOptions.TRAY;
  if (lowerPackage.includes(PackagingOptions.TUBE))
    return PackagingOptions.TUBE;
  return null;
};

export const downloadFile = (
  blob: Blob,
  filename: string,
  fileExtension: string
) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.${fileExtension}`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const logoFromOrgSupplier = (
  supplier: OrgSupplier | PublicSupplier | OrgSupplierLite | null | undefined
) => {
  if (!supplier) return undefined;
  const supplierLogo =
    _.get(supplier, 'logo') ?? _.get(supplier, 'publicSupplier.logo');

  // add localhost for local development when http is missing from supplierLogo
  if (supplierLogo && !supplierLogo.startsWith('http')) {
    return `${API_URL}${supplierLogo}`;
  }

  const supplierDomain =
    (
      _.get(supplier, 'website') ??
      _.get(supplier, 'publicSupplier.website') ??
      ''
    )?.replace(/^(?:https?:\/\/)?(?:www\.)?([^/]+).*$/, '$1') || undefined;
  const brandfetchLink = supplierDomain
    ? `https://cdn.brandfetch.io/${supplierDomain}/w/256/h/256/logo?c=1idC8bMfKAN5L7Uy4xB`
    : undefined;
  return supplierLogo || brandfetchLink || undefined;
};

/**
 * Updates the permission scope for Parts V2 if the provided permission scope
 * belongs to PART_SEARCH or PART_LIBRARY and the `tempParts2Ui` flag is enabled.
 * This ensures compatibility with the new Parts V2 structure.
 *
 * @param {Object} params - The parameters for the function.
 * @param {PermissionScope} params.permissionScope - The original permission scope to check and update.
 * @param {boolean} params.tempParts2Ui - A flag indicating whether to use the new Parts V2 structure.
 * @returns {PermissionScope} - The updated permission scope with the app set to ALL_APP_IDS.PARTS,
 * or the original permission scope if no changes are made.
 */
export const getPartsV2PermissionScope = ({
  permissionScope,
  tempParts2Ui,
}: {
  permissionScope?: PermissionScope;
  tempParts2Ui: boolean;
}) => {
  if (
    tempParts2Ui &&
    [ALL_APP_IDS.PART_SEARCH, ALL_APP_IDS.PART_LIBRARY].includes(
      permissionScope?.app as ALL_APP_IDS
    )
  ) {
    return { ...permissionScope, app: ALL_APP_IDS.PARTS };
  }
  return permissionScope;
};

// This is a helper function to create an sx object without undefined values
// It's used to adapt code that was written for the more permissive sx typing in MUI v5 to the stricter typing in MUI v7
export const createSxWithoutUndefined = (sx: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(sx).filter(([key, value]) => value !== undefined)
  );

export const formatUnlimitedQuant = (
  value: number | string | undefined | null
) => {
  if (value === null || value === undefined) return '';
  if (_.toNumber(value) >= UNLIMITED_QUANTITY) return UNLIMITED_QUANTITY_STRING;
  return _.toNumber(value).toLocaleString('en-US');
};
